import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Archive, FolderOpen, Loader2, Pin, PinOff, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { RangeSelectHint, RowCheckbox, SelectAllCheckbox } from '@/components/ui/range-select';
import { applyToSet, useRangeSelection, type RangeCheckboxProps } from '@/hooks/useRangeSelection';
import { keepOnly } from '@/lib/rangeSelect';
import { cn } from '@/lib/utils';
import { useBulkUpdateFolders, useFolders } from '../hooks/folders';
import type { Folder } from '../api/types';
import { FolderWorkspace } from '../workspace/FolderWorkspace';
import { NewFolderDialog } from '../workspace/FolderDialogs';
import type { FolderPurpose } from '../api/types';
import { FolderProgressBar } from '../workspace/FolderProgressBar';
import { parseFolderTab, useDiscoveryPaths } from '../workspace/paths';
import {
  FOLDER_GROUPS,
  FOLDER_PURPOSES,
  FOLDER_PURPOSE_LABELS,
  archivesInDays,
  archivesInLabel,
  filterFolders,
  folderPurpose,
  groupFolders,
  openLeadCount,
  ownerOf,
  totalLeadCount,
} from '../workspace/folderModel';
import { useArchiveAfterCompleteDays } from '../workspace/hooks';
import { FolderDot } from '../workspace/ui';
import { OutcomeChips, SearchQualityFlag } from '../components/OutcomeChips';
import { useOutcomes } from '../suggestions/hooks';
import { folderQualityNotice, hasOutcome, type Outcome, type SearchQualityNotice } from '../suggestions/model';

/**
 * Folder routes. `/folders/:folderId/:tab` is one folder's workspace; the
 * Discovery root is the folders home. The folder list is the app-wide
 * `useFolders({ includeArchived: true })` query, so a folder created
 * server-side (a lookalike run) resolves here as soon as the sidebar has it.
 */
export function FolderRoute(): JSX.Element {
  const { folderId = '', tab } = useParams();
  const paths = useDiscoveryPaths();
  const { data: folders, isLoading, error } = useFolders({ includeArchived: true });
  const parsedTab = parseFolderTab(tab);

  if (!parsedTab) return <Navigate to={paths.folder(folderId)} replace />;
  const folder = folders?.find((f) => f.publicId === folderId) ?? null;
  if (!folder) {
    if (isLoading) return <p className="py-10 text-center text-sm text-muted-foreground">Loading folder…</p>;
    return (
      <div className="space-y-4">
        <h1 className="text-[26px] font-bold tracking-[-0.03em]">Folder not found</h1>
        <div className="table-frame">
          <EmptyState
            icon={<FolderOpen className="h-6 w-6" strokeWidth={1.5} />}
            title={error ? (error as Error).message : "This folder doesn't exist, or it was deleted."}
            hint="Pick another folder from the sidebar."
          />
        </div>
      </div>
    );
  }
  // Keyed so filters, selection and dialogs never leak from one folder to the next.
  return <FolderWorkspace key={folder.publicId} folder={folder} tab={parsedTab} />;
}

/** `/folders/:folderId` with no tab → the Leads tab. */
export function FolderIndexRedirect(): JSX.Element {
  const { folderId = '' } = useParams();
  const paths = useDiscoveryPaths();
  return <Navigate to={paths.folder(folderId)} replace />;
}

/** Discovery's landing page: every folder that still needs work, with its progress. */
export function FoldersHome(): JSX.Element {
  const { data: allFolders = [], isLoading } = useFolders({ includeArchived: true });
  const paths = useDiscoveryPaths();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const archiveAfterDays = useArchiveAfterCompleteDays();
  const outcomes = useOutcomes();
  // '' = every purpose. Everything below counts only the folders it keeps.
  const [purpose, setPurpose] = useState<FolderPurpose | ''>('');
  const folders = useMemo(() => filterFolders(allFolders, { purpose }), [allFolders, purpose]);
  const groups = useMemo(() => groupFolders(folders, 'recent'), [folders]);
  const working = FOLDER_GROUPS.filter((g) => g.id !== 'archived');
  const openTotal = folders.reduce((n, f) => n + (groups.archived.includes(f) ? 0 : openLeadCount(f)), 0);

  // Multi-select for bulk pin / unpin / archive, in on-screen order.
  const shown = useMemo(() => FOLDER_GROUPS.filter((g) => g.id !== 'archived').flatMap((g) => groups[g.id]), [groups]);
  const shownIds = useMemo(() => shown.map((f) => f.publicId), [shown]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const range = useRangeSelection({
    visible: shownIds,
    isSelected: (id) => selected.has(id),
    onApply: applyToSet(setSelected),
  });
  // An archived folder leaves the page, and the selection with it.
  const shownKey = shownIds.join(',');
  useEffect(() => {
    setSelected((prev) => keepOnly(prev, shownIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey]);
  const picked = shown.filter((f) => selected.has(f.publicId));

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-0.03em]">Folders</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {allFolders.length === 0
              ? 'A folder holds the creators one search brief turns up.'
              : `${openTotal.toLocaleString()} open lead${openTotal === 1 ? '' : 's'} across ${(folders.length - groups.archived.length).toLocaleString()} folders.`}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          New folder
        </Button>
      </header>

      {allFolders.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span id="folders-purpose-filter" className="text-sm text-muted-foreground">
            Folders for
          </span>
          <div
            role="radiogroup"
            aria-labelledby="folders-purpose-filter"
            className="inline-flex flex-wrap rounded-lg border border-input bg-[var(--input-bg)] p-0.5"
          >
            {(['', ...FOLDER_PURPOSES] as const).map((p) => (
              <button
                key={p || 'all'}
                type="button"
                role="radio"
                aria-checked={purpose === p}
                onClick={() => setPurpose(p)}
                className={cn(
                  'h-8 rounded-md px-3 text-xs font-semibold transition-colors pointer-coarse:h-10',
                  purpose === p
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {p ? FOLDER_PURPOSE_LABELS[p] : 'All'}
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Loading folders…</p>
      ) : allFolders.length === 0 ? (
        <div className="table-frame">
          <EmptyState
            icon={<FolderOpen className="h-6 w-6" strokeWidth={1.5} />}
            title="No folders yet"
            hint="Create a folder, then run a search into it to start finding creators."
          />
        </div>
      ) : (
        <FolderBulkBar
          picked={picked}
          headState={range.headState}
          onToggleAll={range.toggleAll}
          onClear={() => setSelected(new Set())}
        />
      )}
      {!isLoading && allFolders.length > 0 && folders.length === 0 && purpose && (
        <p className="py-6 text-sm text-muted-foreground">
          No folders are tagged for {FOLDER_PURPOSE_LABELS[purpose]} yet. Open a folder and choose Set purpose under
          Folder actions.
        </p>
      )}
      {!isLoading &&
        folders.length > 0 &&
        working.map((g) =>
          groups[g.id].length === 0 ? null : (
            <section key={g.id} className="space-y-2">
              <h2 className="text-[15px] font-bold">
                {g.label} <span className="font-normal text-muted-foreground">{groups[g.id].length}</span>
              </h2>
              <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {groups[g.id].map((f) => (
                  <li key={f.publicId}>
                    <FolderCard
                      folder={f}
                      hint={archivesInLabel(archivesInDays(f, archiveAfterDays))}
                      outcome={outcomes.byFolder.get(f.publicId) ?? null}
                      quality={folderQualityNotice(f) ?? outcomes.qualityByFolder.get(f.publicId) ?? null}
                      selected={selected.has(f.publicId)}
                      checkboxProps={range.checkboxProps(f.publicId)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ),
        )}
      {groups.archived.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {groups.archived.length} archived folder{groups.archived.length === 1 ? '' : 's'} in the sidebar under Archived.
        </p>
      )}

      <NewFolderDialog open={creating} onOpenChange={setCreating} onCreated={(f) => navigate(paths.folder(f.publicId))} />
    </div>
  );
}

/**
 * Select-all and the bulk actions for the folder cards. Each action is the
 * single-folder menu's own update, run once per folder; archiving asks first,
 * in place, because it pauses the folders' schedules.
 */
function FolderBulkBar({
  picked,
  headState,
  onToggleAll,
  onClear,
}: {
  picked: Folder[];
  headState: 'none' | 'some' | 'all';
  onToggleAll: () => void;
  onClear: () => void;
}): JSX.Element {
  const bulk = useBulkUpdateFolders();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const toPin = picked.filter((f) => !f.pinnedAt);
  const toUnpin = picked.filter((f) => f.pinnedAt);
  const n = (count: number) => `${count} folder${count === 1 ? '' : 's'}`;
  useEffect(() => {
    if (picked.length === 0) setConfirmArchive(false);
  }, [picked.length]);

  function run(folders: Folder[], patch: { archived?: boolean; pinned?: boolean }, verb: string, done: string) {
    if (folders.length === 0 || bulk.isPending) return;
    const id = toast.loading(`${verb} 0 of ${n(folders.length)}…`);
    bulk.mutate(
      {
        publicIds: folders.map((f) => f.publicId),
        patch,
        onProgress: (i, total) => toast.loading(`${verb} ${i} of ${n(total)}…`, { id }),
      },
      {
        onSuccess: ({ updated, failed }) => {
          setConfirmArchive(false);
          if (failed.length === 0) {
            toast.success(`${done} ${n(updated)}`, { id });
            onClear();
            return;
          }
          const names = folders.filter((f) => failed.includes(f.publicId)).map((f) => `“${f.name}”`);
          toast.error(`${done} ${n(updated)}; ${failed.length} failed`, {
            id,
            description: `${names.slice(0, 3).join(', ')}${names.length > 3 ? ` and ${names.length - 3} more` : ''} — try again.`,
          });
        },
      },
    );
  }

  return (
    <div className="space-y-2">
      <div
        role="group"
        aria-label="Folder selection"
        className={cn(
          'flex min-h-9 flex-wrap items-center gap-2 rounded-lg px-2 py-1',
          picked.length > 0 && 'border border-brand/30 bg-brand/10',
        )}
      >
        <label className="inline-flex cursor-pointer items-center gap-2 px-1 text-sm">
          <SelectAllCheckbox label="Select every folder shown" state={headState} onToggle={onToggleAll} />
          <span className={picked.length > 0 ? 'font-semibold' : 'text-muted-foreground'}>
            {picked.length > 0 ? `${n(picked.length)} selected` : 'Select all'}
          </span>
        </label>
        {picked.length > 0 && (
          <>
            {toPin.length > 0 && (
              <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => run(toPin, { pinned: true }, 'Pinning', 'Pinned')}>
                <Pin className="h-3.5 w-3.5" />
                Pin {toPin.length}
              </Button>
            )}
            {toUnpin.length > 0 && (
              <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => run(toUnpin, { pinned: false }, 'Unpinning', 'Unpinned')}>
                <PinOff className="h-3.5 w-3.5" />
                Unpin {toUnpin.length}
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={bulk.isPending || confirmArchive} onClick={() => setConfirmArchive(true)}>
              <Archive className="h-3.5 w-3.5" />
              Archive {picked.length}
            </Button>
            <RangeSelectHint />
            <Button size="sm" variant="ghost" className="ml-auto" disabled={bulk.isPending} onClick={onClear}>
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          </>
        )}
      </div>
      {confirmArchive && picked.length > 0 && (
        <div
          role="alertdialog"
          aria-label={`Archive ${n(picked.length)}?`}
          className="flex flex-wrap items-center gap-3 rounded-lg border border-warning/35 bg-warning/10 px-3 py-2 text-sm"
        >
          <span className="min-w-0 flex-1">
            Archive {n(picked.length)}? Their schedules pause and they move to Archived in the sidebar, where each can be
            restored.
          </span>
          <Button size="sm" variant="ghost" disabled={bulk.isPending} onClick={() => setConfirmArchive(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={bulk.isPending} onClick={() => run(picked, { archived: true }, 'Archiving', 'Archived')}>
            {bulk.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Archive {n(picked.length)}
          </Button>
        </div>
      )}
    </div>
  );
}

function FolderCard({
  folder,
  hint,
  outcome,
  quality,
  selected,
  checkboxProps,
}: {
  folder: Folder;
  hint: string | null;
  /** What outreach got out of this folder; null until the server reports it. */
  outcome: Outcome | null;
  quality: SearchQualityNotice | null;
  selected: boolean;
  checkboxProps: RangeCheckboxProps;
}): JSX.Element {
  const paths = useDiscoveryPaths();
  const open = openLeadCount(folder);
  const total = totalLeadCount(folder);
  const owner = ownerOf(folder);
  // The checkbox sits beside the link, not in it: a box inside an <a> would navigate.
  return (
    <div
      className={cn(
        'panel flex h-full rounded-xl transition-colors',
        selected ? 'bg-brand/10 ring-1 ring-brand/40' : 'hover:bg-accent/40',
      )}
    >
      <RowCheckbox
        aria-label={`Select ${folder.name}`}
        hitClassName="m-0 items-start self-stretch pb-4 pl-4 pr-1 pt-5"
        {...checkboxProps}
      />
      <Link
        to={paths.folder(folder.publicId)}
        className="block min-w-0 flex-1 space-y-3 rounded-xl p-4 pl-2"
      >
        <div className="flex items-center gap-2">
          <FolderDot folder={folder} />
          <span className="min-w-0 flex-1 truncate font-semibold" title={folder.name}>
            {folder.name}
          </span>
          <span className="text-sm tabular-nums text-muted-foreground">
            {open.toLocaleString()} open
          </span>
        </div>
        <FolderProgressBar counts={folder.counts} className="[&_ul]:hidden" />
        <p className="text-sm text-muted-foreground">
          {[
            `${total.toLocaleString()} lead${total === 1 ? '' : 's'}`,
            // Clipping is the default, so only the exceptions are called out.
            folderPurpose(folder) === 'clipping' ? null : `For ${FOLDER_PURPOSE_LABELS[folderPurpose(folder)]}`,
            owner,
            hint,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {(hasOutcome(outcome) || quality) && (
          <div className="flex flex-wrap items-center gap-1.5">
            <OutcomeChips outcome={outcome} />
            <SearchQualityFlag notice={quality} />
          </div>
        )}
      </Link>
    </div>
  );
}
